import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { ArrowLeft, FileText } from 'lucide-react';
import BottomNav from '../components/BottomNav';

const TermsOfService = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleBack = () => {
    // If user is logged in, go back to settings, otherwise go to landing
    if (user) {
      navigate('/settings');
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-blue-50 to-teal-50 dark:from-slate-900 dark:to-slate-800 py-8 px-4 transition-colors">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">{user ? 'Back to Settings' : 'Back to Home'}</span>
          </button>
          
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 dark:from-cyan-600 dark:to-blue-500 flex items-center justify-center transition-colors">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 dark:from-cyan-400 dark:to-blue-400 bg-clip-text text-transparent transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>
              Terms of Service
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400 transition-colors dark:text-gray-400 transition-colors">Last updated: October 18, 2025</p>
        </div>

        {/* Content */}
        <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg border-0 dark:border-gray-700 p-8 transition-colors">
          <div className="prose prose-slate max-w-none">
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 transition-colors">1. Acceptance of Terms</h2>
              <p className="text-gray-700 dark:text-gray-300 transition-colors dark:text-gray-300 leading-relaxed transition-colors">
                Welcome to Logly. By accessing or using our time tracking application and services ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not use our Service.
              </p>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed mt-3">
                These Terms apply to all users, including visitors, registered users, and subscribers. By creating an account, you represent that you are at least 13 years old and have the legal capacity to enter into these Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 transition-colors">2. Description of Service</h2>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed mb-3">
                Logly is a time tracking application that allows users to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 transition-colors">
                <li>Log time entries manually or via voice commands</li>
                <li>Create and manage projects</li>
                <li>Track time with desktop timers</li>
                <li>Generate reports and export data</li>
                <li>Use AI-powered project predictions</li>
                <li>Access calendar and weekly summary views</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed mt-3">
                We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time, with or without notice.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 transition-colors">3. Account Registration</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3 transition-colors">3.1 Account Creation</h3>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed mb-3">
                To use Logly, you must create an account by providing accurate and complete information. You agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 transition-colors">
                <li>Provide truthful and accurate information</li>
                <li>Maintain the security of your password</li>
                <li>Notify us immediately of any unauthorized access</li>
                <li>Accept responsibility for all activities under your account</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3 transition-colors mt-6">3.2 Account Termination</h3>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
                We reserve the right to suspend or terminate your account if you violate these Terms or engage in fraudulent, abusive, or illegal activities.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 transition-colors">4. Subscription and Billing</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3 transition-colors">4.1 Free Trial</h3>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
                New users receive a 7-day free trial to explore all features. No payment information is required to start the trial. After the trial period, you must subscribe to continue using the Service.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3 transition-colors mt-6">4.2 Subscription Plans</h3>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed mb-3">
                Logly offers a monthly subscription plan at $2.99 per month. By subscribing, you agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 transition-colors">
                <li>Pay the subscription fee monthly via Stripe</li>
                <li>Automatic renewal unless cancelled</li>
                <li>Pricing subject to change with 30 days notice</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3 transition-colors mt-6">4.3 Payment Processing</h3>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
                All payments are processed securely through Stripe. We do not store your payment card information. You authorize us to charge your payment method for subscription fees and any applicable taxes.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3 transition-colors mt-6">4.4 Cancellation and Refunds</h3>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
                You may cancel your subscription at any time through the Stripe Customer Portal in Settings. Upon cancellation, you will retain access until the end of your current billing period. We do not provide refunds for partial months.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 transition-colors">5. Acceptable Use</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3 transition-colors">5.1 Permitted Use</h3>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
                You may use Logly for lawful time tracking and project management purposes consistent with these Terms.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3 transition-colors mt-6">5.2 Prohibited Conduct</h3>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed mb-3">
                You agree NOT to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 transition-colors">
                <li>Use the Service for any illegal purpose</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Interfere with or disrupt the Service</li>
                <li>Upload viruses, malware, or harmful code</li>
                <li>Scrape, copy, or reverse engineer the Service</li>
                <li>Share your account with others</li>
                <li>Use automated tools to access the Service without permission</li>
                <li>Violate any applicable laws or regulations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 transition-colors">6. Intellectual Property</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3 transition-colors">6.1 Ownership</h3>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
                Logly and its original content, features, and functionality are owned by us and are protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, or distribute any part of our Service without permission.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3 transition-colors mt-6">6.2 Your Content</h3>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
                You retain ownership of the data you create in Logly (time entries, projects, notes). By using our Service, you grant us a license to use, store, and process your content solely to provide the Service.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3 transition-colors mt-6">6.3 Feedback</h3>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
                If you provide feedback, suggestions, or ideas about the Service, we may use them without any obligation to compensate you.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 transition-colors">7. Privacy and Data Protection</h2>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
                Your privacy is important to us. Our Privacy Policy explains how we collect, use, and protect your personal information. By using Logly, you consent to our data practices as described in the Privacy Policy.
              </p>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed mt-3">
                You can view our full Privacy Policy at: <a href="/privacy" className="text-cyan-600 hover:text-cyan-700">logly.work/privacy</a>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 transition-colors">8. Disclaimers and Limitations of Liability</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3 transition-colors">8.1 Service Availability</h3>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
                The Service is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not guarantee that the Service will be uninterrupted, secure, or error-free.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3 transition-colors mt-6">8.2 Accuracy of Data</h3>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
                While we strive for accuracy in our AI-powered features (voice transcription, project predictions), we cannot guarantee 100% accuracy. You are responsible for reviewing and correcting your time entries.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3 transition-colors mt-6">8.3 Limitation of Liability</h3>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
                To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from your use of the Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 transition-colors">9. Indemnification</h2>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
                You agree to indemnify, defend, and hold harmless Logly and its officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses arising out of or related to your use of the Service, violation of these Terms, or infringement of any rights of another party.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 transition-colors">10. Third-Party Services</h2>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
                Our Service integrates with third-party services (Stripe for payments, OpenAI for voice processing). Your use of these services is subject to their respective terms and conditions. We are not responsible for the actions or policies of third-party services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 transition-colors">11. Modifications to Terms</h2>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
                We reserve the right to modify these Terms at any time. We will notify you of material changes by posting the updated Terms on this page and updating the "Last updated" date. Your continued use of the Service after changes constitutes acceptance of the new Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 transition-colors">12. Termination</h2>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed mb-3">
                Either party may terminate this agreement at any time:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 transition-colors">
                <li><strong>You:</strong> By canceling your subscription and deleting your account</li>
                <li><strong>Us:</strong> By providing notice if you violate these Terms</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed mt-3">
                Upon termination, your right to use the Service will cease immediately. We may delete your data in accordance with our data retention policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 transition-colors">13. Governing Law and Dispute Resolution</h2>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which we operate, without regard to conflict of law principles. Any disputes arising from these Terms or the Service shall be resolved through binding arbitration, except where prohibited by law.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 transition-colors">14. Severability</h2>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
                If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 transition-colors">15. Entire Agreement</h2>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
                These Terms, together with our Privacy Policy, constitute the entire agreement between you and Logly regarding the use of the Service and supersede any prior agreements.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 transition-colors">16. Contact Information</h2>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
                If you have questions about these Terms, please contact us:
              </p>
              <div className="mt-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors">
                <p className="text-gray-700 dark:text-gray-300 transition-colors"><strong>Email:</strong> <a href="mailto:support@logly.work" className="text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 transition-colors">support@logly.work</a></p>
                <p className="text-gray-700 dark:text-gray-300 transition-colors mt-1"><strong>Website:</strong> <a href="https://logly.work" className="text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 transition-colors">https://logly.work</a></p>
              </div>
            </section>

            <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-l-4 border-blue-500 dark:border-blue-400 transition-colors">
              <p className="text-sm text-gray-700 dark:text-gray-300 transition-colors">
                By using Logly, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
              </p>
            </div>

          </div>
        </Card>
      </div>

      <BottomNav />
    </div>
  );
};

export default TermsOfService;
